import React, { useEffect, useMemo, useState } from "react";
import { api, fmtCurrency } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Clock, CalendarBlank, ArrowRight, MagnifyingGlass } from "@phosphor-icons/react";

const CATEGORY_TONE = {
  discovery:    { dot: "bg-indigo-500",  chip: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  intake:       { dot: "bg-blue-500",    chip: "bg-blue-50 text-blue-700 border-blue-200" },
  sow:          { dot: "bg-violet-500",  chip: "bg-violet-50 text-violet-700 border-violet-200" },
  signing:      { dot: "bg-amber-500",   chip: "bg-amber-50 text-amber-700 border-amber-200" },
  won:          { dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  delivery:     { dot: "bg-orange-500",  chip: "bg-orange-50 text-orange-700 border-orange-200" },
  invoicing:    { dot: "bg-cyan-500",    chip: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  change_order: { dot: "bg-pink-500",    chip: "bg-pink-50 text-pink-700 border-pink-200" },
  status:       { dot: "bg-slate-500",   chip: "bg-slate-50 text-slate-700 border-slate-200" },
  other:        { dot: "bg-slate-400",   chip: "bg-slate-50 text-slate-600 border-slate-200" },
};

const CATEGORY_LABEL = {
  discovery: "Discovery", intake: "Intake", sow: "SOW", signing: "Signing",
  won: "Won", delivery: "Delivery", invoicing: "Invoicing", change_order: "Change Order",
  status: "Status", other: "Other",
};

export default function Timeline() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [data, setData] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState("");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data || [])).catch(() => toast.error("Failed to load clients"));
  }, []);

  const loadTimeline = async (cid, deal = "") => {
    if (!cid) return;
    setLoading(true);
    try {
      const params = deal ? `?deal=${encodeURIComponent(deal)}` : "";
      const r = await api.get(`/timeline/client/${cid}${params}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load timeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (selectedClientId) loadTimeline(selectedClientId, selectedDeal); }, [selectedClientId, selectedDeal]);

  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients.slice(0, 60);
    return clients.filter(c => (c.company_name || "").toLowerCase().includes(q) || (c.ps_code || "").toLowerCase().includes(q)).slice(0, 60);
  }, [clients, search]);

  const visibleEvents = useMemo(() => {
    if (!data?.events) return [];
    return category === "all" ? data.events : data.events.filter(e => e.category === category);
  }, [data, category]);

  return (
    <div className="p-6 space-y-6" data-testid="timeline-page">
      <div>
        <h1 className="text-2xl font-semibold text-[#0F1E38]">Project Timeline</h1>
        <p className="text-sm text-slate-500 mt-1">Reviewer sheet · every state transition across the discovery → delivery journey, with time-in-state.</p>
      </div>

      {/* Client picker + filters */}
      <div className="border border-slate-200 bg-white rounded-lg p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[240px]">
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1 block">Search client</label>
          <div className="relative">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400"/>
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Company name or ps_code…" className="pl-8" data-testid="timeline-search"/>
          </div>
          {search && filteredClients.length > 0 && (
            <div className="mt-1 max-h-56 overflow-y-auto border border-slate-200 rounded bg-white shadow-sm">
              {filteredClients.map(c => (
                <button key={c.id} data-testid={`timeline-pick-client-${c.id}`}
                  onClick={() => { setSelectedClientId(c.id); setSearch(""); setSelectedDeal(""); }}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b border-slate-100 flex justify-between">
                  <span className="font-medium">{c.company_name}</span>
                  <span className="text-slate-400 font-mono">{c.ps_code}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        {data && data.deals.length > 1 && (
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1 block">Deal</label>
            <Select value={selectedDeal || "all"} onValueChange={v => setSelectedDeal(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56 h-9"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All deals ({data.deals.length})</SelectItem>
                {data.deals.map(d => (<SelectItem key={d.ps_code || d.project_id} value={d.ps_code}>{d.project_name} · {d.ps_code}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div>
          <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1 block">Category</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-44 h-9" data-testid="timeline-category-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.entries(CATEGORY_LABEL).map(([k,v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Metrics header */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="timeline-metrics">
          <Metric label="Calendar days" value={`${data.metrics.total_calendar_days} d`} icon={<CalendarBlank size={16}/>}/>
          <Metric label="Total events" value={data.metrics.event_count} icon={<Clock size={16}/>}/>
          <Metric label="Revision rounds" value={data.metrics.revision_rounds}/>
          <Metric label="Change orders" value={data.metrics.change_orders}/>
        </div>
      )}

      {/* Vertical timeline */}
      {loading && <div className="text-sm text-slate-500 text-center py-12">Loading…</div>}
      {!loading && data && visibleEvents.length === 0 && (
        <div className="text-sm text-slate-500 text-center py-12">No events for this filter.</div>
      )}
      {!loading && visibleEvents.length > 0 && (
        <div className="border border-slate-200 bg-white rounded-lg p-6" data-testid="timeline-events">
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200"/>
            <div className="space-y-4">
              {visibleEvents.map((e, i) => {
                const tone = CATEGORY_TONE[e.category] || CATEGORY_TONE.other;
                return (
                  <div key={i} className="relative pl-12" data-testid={`timeline-event-${i}`}>
                    <div className={`absolute left-2.5 top-1.5 w-3 h-3 rounded-full ${tone.dot} ring-4 ring-white`}/>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded border ${tone.chip}`}>{CATEGORY_LABEL[e.category] || e.category}</span>
                          <span className="text-sm font-medium text-[#0F1E38]">{e.title}</span>
                        </div>
                        {e.subtitle && <div className="text-xs text-slate-500 mt-1">{e.subtitle}</div>}
                        <div className="text-[10px] text-slate-400 mt-1 font-mono flex items-center gap-2">
                          <span>{new Date(e.at).toLocaleString()}</span>
                          <span>· by {e.actor}</span>
                          {e.ps_code && <span>· {e.ps_code}</span>}
                        </div>
                      </div>
                      {e.delta_pill && (
                        <span title="Time since the previous event" className="shrink-0 text-[10px] font-mono uppercase px-2 py-1 rounded bg-slate-50 border border-slate-200 text-slate-600 flex items-center gap-1">
                          <ArrowRight size={10}/> {e.delta_pill}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {!data && !loading && (
        <div className="text-sm text-slate-500 text-center py-16 border border-dashed border-slate-200 rounded-lg">
          Pick a client above to see their timeline.
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, icon }) {
  return (
    <div className="border border-slate-200 bg-white rounded-lg p-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1">{icon} {label}</div>
      <div className="text-xl font-semibold text-[#0F1E38] mt-1">{value}</div>
    </div>
  );
}
